import React, { useEffect, useRef } from 'react';
import { useMotionValue, useTransform, animate } from 'framer-motion';
import { useReducedMotionPreferred } from '../../hooks/useReducedMotionPreferred';

/**
 * AnimatedNumber
 * Counts from previous value to `value` using framer-motion.
 * Snaps immediately when reduced motion is preferred.
 *
 * @param {number} value
 * @param {(n: number) => string} format - optional formatter
 * @param {number} duration - animation duration in seconds (default 0.8)
 * @param {string} [className]
 */
function AnimatedNumber({ value, format, duration = 0.8, className, style }) {
  const reducedMotion = useReducedMotionPreferred();
  const motionValue = useMotionValue(value);
  const displayRef = useRef(null);

  const formatted = useTransform(motionValue, (v) => {
    return format ? format(v) : String(Math.round(v));
  });

  useEffect(() => {
    if (reducedMotion) {
      motionValue.set(value);
      return;
    }
    const controls = animate(motionValue, value, { duration, ease: 'easeOut' });
    return controls.stop;
  }, [value, duration, reducedMotion, motionValue]);

  useEffect(() => {
    return formatted.on('change', (v) => {
      if (displayRef.current) {
        displayRef.current.textContent = v;
      }
    });
  }, [formatted]);

  return (
    <span ref={displayRef} className={className} style={style}>
      {format ? format(value) : String(Math.round(value))}
    </span>
  );
}

export default AnimatedNumber;
